import React, { Fragment, useState } from 'react'
import { TextField, Button, Box } from '@mui/material'
import { putAwayActions } from '../../../actions/putAwayActions'
import { useConfiguration } from '../../../../../hooks/useConfiguration'
import { usePutAway } from '../../../Context/usePutAway'
import { Pagination } from '../../../../Pagination'
import { usePagination } from '../../../../../hooks/usePagination'
import { getAsnBatches } from './api'
import { BatchTable } from '../../../../_WMS/ListBatch/BatchTable'
import { Batch } from '../../../../../interfaces/Batch'

interface FefoModalProps {
  asnId: number
  lpn?: string
}

export const FefoModal: React.FC<FefoModalProps> = ({ asnId, lpn }) => {
  const { configurationState } = useConfiguration()
  const { state, dispatch } = usePutAway()
  const { skuFefoType, currentBatches } = state
  const [batchToSearch, setBatchToSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchMessage, setSearchMessage] = useState('')
  const [searchIsError, setSearchIsError] = useState(false)
  const [searchResults, setSearchResults] = useState<Batch[] | null>(null)

  const isPutawayUnitByUnit = configurationState.configuration.filter(
    (el) => el.name === 'UnitaryPutaway'
  )[0]

  const pagination = usePagination(5)

  const {
    addBatchInfoToState,
    getSkuIdFromElegibleProducts,
    filterCompletedBatchesInFrontend
  } = putAwayActions()

  // Paginación local sobre currentBatches o searchResults
  const getLocalBatches = () => {
    // Modo búsqueda activa: mostrar solo resultados de búsqueda (sin paginación)
    if (searchResults !== null) {
      return {
        items: searchResults,
        totalItems: searchResults.length
      }
    }

    // Modo normal: mostrar cache completo con paginación
    const availableBatches = filterCompletedBatchesInFrontend(currentBatches)

    const startIndex = pagination.page * pagination.pageSize
    const endIndex = startIndex + pagination.pageSize

    const paginatedBatches = availableBatches.slice(startIndex, endIndex)

    return {
      items: paginatedBatches,
      totalItems: availableBatches.length
    }
  }

  // Búsqueda específica SIEMPRE va a API (para datos frescos)
  const handleSearch = async () => {
    if (!batchToSearch.trim()) {
      return
    }

    const skuId = getSkuIdFromElegibleProducts(state.skuEan.value)

    try {
      setSearching(true)
      setSearchMessage('')
      setSearchIsError(false)

      const response = await getAsnBatches(
        0,
        1, // Búsqueda exacta: solo 1 resultado esperado
        asnId,
        lpn,
        batchToSearch,
        skuId
        // Sin filtro de estados: busca en todos los estados
      )

      const foundBatches = response.data.items || []

      if (foundBatches.length === 0) {
        // No existe o está completado
        setSearchMessage('Lote no disponible o ya completado')
        setSearchIsError(true)
        setSearchResults(null) // Volver a modo normal
      } else {
        // Guardar resultados en estado de búsqueda (modo búsqueda activa)
        setSearchResults(foundBatches)

        // Volver a página 0 para ver los resultados
        pagination.setPagination(0, pagination.pageSize)

        // Mensaje de éxito
        setSearchMessage('Lote encontrado. Haz click para seleccionar')
        setSearchIsError(false)
      }
    } catch (error) {
      setSearchMessage('Error al buscar el lote')
      setSearchIsError(true)
      setSearchResults(null)
    } finally {
      setSearching(false)
    }
  }

  // Mostrar todos los lotes: invalida cache y hace fetch nuevo sin filtros de estado
  const handleRefresh = async () => {
    const skuId = getSkuIdFromElegibleProducts(state.skuEan.value)

    try {
      setSearching(true)

      // Limpiar resultados de búsqueda (volver a modo normal)
      setSearchResults(null)

      // Invalidar cache
      dispatch({ type: 'invalidateBatchCache' })

      // Fetch nuevo con 500 lotes SIN filtro de estados (incluye completados estado 4)
      const response = await getAsnBatches(
        0,
        500,
        asnId,
        lpn,
        '',
        skuId
        // Sin filtro de estados: incluye todos los estados (1, 2, 3, 4)
      )

      const batchesFromApi = response.data.items || []

      // Guardar en cache con timestamp nuevo
      dispatch({
        type: 'setCurrentBatchesWithTimestamp',
        payload: {
          batches: batchesFromApi,
          timestamp: Date.now()
        }
      })

      // Resetear búsqueda y volver a página 1
      setBatchToSearch('')
      pagination.setPagination(0, pagination.pageSize)
      setSearchMessage('')
      setSearchIsError(false)
    } catch (error) {
      setSearchMessage('Error al refrescar los lotes')
      setSearchIsError(true)
    } finally {
      setSearching(false)
    }
  }

  if (asnId === 0) {
    return <p>No se ha seleccionado Asn</p>
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    pagination.setPagination(auxPage, pageSize)
  }

  const getSearchPlaceholder = () => {
    switch (skuFefoType.modalType) {
      case 'batch':
        return 'Escanear lote...'
      case 'expiration':
        return 'Buscar fecha de vencimiento...'
      case 'both':
        return 'Escanear lote o fecha...'
      default:
        return 'Escanear lote...'
    }
  }

  const getEmptyText = () => {
    switch (skuFefoType.modalType) {
      case 'batch':
        return 'Ajusta los filtros para ver lotes'
      case 'expiration':
        return 'Ajusta los filtros para ver fechas de vencimiento'
      case 'both':
        return 'No se encontraron coincidencias'
      default:
        return 'No se encontraron coincidencias'
    }
  }

  // Determinar qué datos mostrar (siempre local)
  const localData = getLocalBatches()
  const batchesToShow = localData.items
  const totalItems = localData.totalItems

  return (
    <Fragment>
      <TextField
        fullWidth
        variant='outlined'
        placeholder={getSearchPlaceholder()}
        value={batchToSearch}
        onChange={(e) => {
          setBatchToSearch(e.target.value)
          setSearchMessage('')
          setSearchIsError(false)
          setSearchResults(null) // Limpiar búsqueda activa al escribir
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleSearch()
          }
        }}
        error={searchIsError}
        helperText={searchMessage}
      />
      <Box mt={2} mb={2}>
        <Button
          fullWidth
          variant='outlined'
          color='secondary'
          onClick={handleRefresh}
        >
          Mostrar todos los lotes
        </Button>
      </Box>
      <BatchTable
        batches={batchesToShow}
        showBatchNumber={skuFefoType.hasBatchNumber}
        showExpirationDate={skuFefoType.hasExpirationDate}
        modalType={skuFefoType.modalType}
        isLoading={searching}
        onBatchClick={(batch) =>
          addBatchInfoToState(batch, isPutawayUnitByUnit)
        }
        emptyText={getEmptyText()}
      />
      <Pagination
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalItem: totalItems
        }}
        onPaginationChange={handlePaginationChange}
      />
    </Fragment>
  )
}
